import { S2P_ELEVATE_PROGRAM, type S2PElevateSlide } from "@/data/s2pElevateAI";
import { S2P_COUNCIL_SLIDES, S2P_COUNCIL_STEPS } from "@/data/s2pAIBusinessCouncilAugust";
import { reviseElevateLearnerCopy } from "@/data/elevateLearnerCopy";
import {
  ELEVATE_BUILD_OUTPUT_SLIDES, ELEVATE_TOKEN_BUDGET_SLIDE,
  ELEVATE_PRODUCTIVITY_SLIDE, ELEVATE_CONTINUOUS_IMPROVEMENT_SLIDE,
} from "@/data/elevateBuildOutputs";
import { applyElevateCoreVoice } from "@/data/elevateCoreVoice";
import { RECORD_SKILL_SLIDE } from "@/data/elevateRecordSkill";
import { applyElevateFacilitation, applyElevateFacilitationTiming } from "@/data/elevateFacilitation";
import { applyElevatePromptFormat, applyElevateShowcasePromptFormat } from "@/data/elevatePromptFormat";
import { applyAgentSetupSession, replaceElevateSessionFour } from "@/data/elevateAgentSetup";
import { addElevateHomework } from "@/data/elevateHomework";

export const COUNCIL_CHIEF_OF_STAFF_SLIDE = S2P_COUNCIL_SLIDES.find((slide) => slide.id === "chief-of-staff")!;
export const COUNCIL_CHIEF_OF_STAFF_STEP = S2P_COUNCIL_STEPS.find((step) => step.id === "chief-of-staff")!;

// These moves belong to /s2p-elevate-ai. Keep source IDs and the shared journey
// intact so audience-specific decks and existing links retain their content.
const toolSelectionSlideIds = new Set([
  "agent-access-ladder", "showcase-logged-in-browser",
]);
const toolSelectionSlides = new Map(S2P_ELEVATE_PROGRAM.slides
  .filter((slide) => toolSelectionSlideIds.has(slide.id))
  .map((slide) => [slide.id, { ...slide, session: 3 as const }]));

const slides = S2P_ELEVATE_PROGRAM.slides
  .filter((slide) => !["ai-swot-library", "level-fit-huddle", "browser-extension-setup"].includes(slide.id) && !toolSelectionSlideIds.has(slide.id))
  .flatMap((slide) => {
    if (slide.id === "connections-and-tools") return [slide, toolSelectionSlides.get("agent-access-ladder")!];
    if (slide.id === "ai-setup-choices") return [
      slide,
      toolSelectionSlides.get("showcase-logged-in-browser")!,
    ];
    if (slide.id === "showcase-microsoft-researcher-analyst") return [slide, RECORD_SKILL_SLIDE];
    if (slide.id === "session-5-metric-loop-support-example") return [slide, ELEVATE_TOKEN_BUDGET_SLIDE, ELEVATE_PRODUCTIVITY_SLIDE];
    if (slide.id === "feedback-and-schedule") return [slide, ELEVATE_CONTINUOUS_IMPROVEMENT_SLIDE];
    return [slide];
  }).map((slide): S2PElevateSlide => {
    if (slide.id === "ai-workflow-pilot") return {
      ...slide,
      note: "Use the closing discussion to define the comparison. Run the supervised candidate in Session 4; evaluate the evidence in Session 5. Sample size and thresholds must fit your work.",
    };
    if (slide.id === "generate-install") return {
      ...slide,
      lead: "Bring the Skill recorded in Session 3, or ask ChatGPT or Claude to draft the method for your selected Job. Review its instructions and controls before testing with a new input.",
    };
    if (slide.id === "session-3-do-this-first") return {
      ...slide,
      sections: slide.sections?.map((section) => section.title === "Prepare one test" ? {
        ...section,
        body: "If you recorded the Skill in Session 3, use a new approved input. Otherwise, test your written Skill. Include a missing owner or conflicting date; the example below supplies both.",
      } : section),
    };
    return slide.id === "privacy-team-rule" ? {
      id: slide.id,
      session: slide.session,
      kind: "prompt",
      tone: "paper",
      title: COUNCIL_CHIEF_OF_STAFF_SLIDE.title,
      eyebrow: COUNCIL_CHIEF_OF_STAFF_SLIDE.eyebrow,
      promptLabel: COUNCIL_CHIEF_OF_STAFF_STEP.prompts![0].label,
      prompt: COUNCIL_CHIEF_OF_STAFF_STEP.prompts![0].value,
      sourceIds: ["chief-of-staff"],
    } : slide;
  }).map((slide) => applyElevateCoreVoice(applyElevatePromptFormat(applyElevateFacilitation(slide))));

// Keep the shared Council source intact; the ELEVATE copy uses its own prompt opening.
export const ELEVATE_CHIEF_OF_STAFF_SLIDE = reviseElevateLearnerCopy(COUNCIL_CHIEF_OF_STAFF_SLIDE);
export const ELEVATE_CHIEF_OF_STAFF_STEP = reviseElevateLearnerCopy({
  ...COUNCIL_CHIEF_OF_STAFF_STEP,
  prompts: COUNCIL_CHIEF_OF_STAFF_STEP.prompts!.map((prompt, index) => index === 0 ? {
    ...prompt, value: slides.find((slide) => slide.id === "privacy-team-rule")!.prompt!,
  } : prompt),
});
const showcase = S2P_ELEVATE_PROGRAM.showcase.map(applyElevateShowcasePromptFormat);

// Fund Session 3 demonstrations from existing example time. The audience-only
// Session 4 replacement below preserves the shared Longhand curriculum.
const sessions = S2P_ELEVATE_PROGRAM.sessions.map((session) => {
  if (session.id === 3) return {
    ...session,
    teachingPlan: session.teachingPlan?.flatMap((part) => part.title === "Compare tool fit and work through three use cases" ? [
      { title: "Demonstrate an approved browser task", minutes: 2 },
      { title: "Compare the demonstrated paths; optional examples", minutes: 15 },
      { title: "Record a workflow as a Skill", minutes: RECORD_SKILL_SLIDE.durationMinutes! },
    ] : [part]),
  };
  if (session.id === 5) return {
    ...session,
    teachingPlan: session.teachingPlan?.flatMap((part) => part.title === "Review and improvement, including two use cases" ? [
      { title: "Track the run against an explicit budget", minutes: ELEVATE_TOKEN_BUDGET_SLIDE.durationMinutes! },
      { title: "Estimate productivity gains from visible evidence", minutes: ELEVATE_PRODUCTIVITY_SLIDE.durationMinutes! },
      { title: "Review agent skills using session evidence", minutes: ELEVATE_CONTINUOUS_IMPROVEMENT_SLIDE.durationMinutes! },
      { ...part, minutes: part.minutes - ELEVATE_TOKEN_BUDGET_SLIDE.durationMinutes! - ELEVATE_PRODUCTIVITY_SLIDE.durationMinutes! - ELEVATE_CONTINUOUS_IMPROVEMENT_SLIDE.durationMinutes! },
    ] : [part]),
  };
  return session;
}).map(applyElevateFacilitationTiming).map((session) => applyAgentSetupSession(session)).map((session) => session.id === 4 ? {
  ...session,
  // Fund the six-minute interface review from setup, test, and schedule examples.
  // Keep the agent setup sequence and the 15-minute activity intact.
  teachingPlan: session.teachingPlan?.flatMap((part) => part.title === "Optional examples with the same review steps" ? [
    part,
    { title: "Build and review with Claude Artifacts and ChatGPT Sites", minutes: 6 },
  ] : [{
    ...part,
    minutes: part.minutes - (["Set up, save, and open it again", "Try a run and see how it handles gaps", "Set the schedule, review, and pause"].includes(part.title) ? 2 : 0),
  }]),
} : session);

const agentSetupSlides = replaceElevateSessionFour(slides).flatMap((slide) => {
  if (slide.id === "session-3-do-this-first") return [
    ...ELEVATE_BUILD_OUTPUT_SLIDES.map(applyElevateCoreVoice),
    {
      ...slide,
      note: `${slide.note ?? ""} Optional: present the same reviewed output as an Artifact or Site. Keep the inputs, issue flags, and review checks constant; use the written sample if build access is unavailable.`.trim(),
    },
  ];
  return [slide];
});

const slidesWithHomework = addElevateHomework(agentSetupSlides);

export const S2P_ELEVATE_CORE_PROGRAM = reviseElevateLearnerCopy({
  ...S2P_ELEVATE_PROGRAM,
  curriculum: { ...S2P_ELEVATE_PROGRAM.curriculum, sessions, slides: slidesWithHomework, showcase },
  sessions,
  slides: slidesWithHomework,
  showcase,
});
